import {ConsulationFlowType} from '@/constants';
import {GetConsultationSpecialistListRequestPayload} from '@/interface';
import {
  appSpecializations,
  patientDashboardCategory,
  patientDashboardCurrentStep,
  patientDashboardConsultation,
  patientDashboardConsultationFlow,
  patientDashboardManualCategorySelectionFlag,
  patientDashboardHospitalSpecializations,
  patientDashboardSpecialists,
  allAppSpecializations,
  patientDashboardOriginalCategory,
  patientDashboardSuccessState,
} from '@/services/redux/selectors';
import {getSpecialistListRequest, manualCategorySelectionRequest, getPendingConsultationCaseRequest} from '@/services/redux/slices';
import {Icon, IconType, SelectInput} from '@/ui-kits';
import {Checkbox} from '@/ui-kits/Checkbox';
import {cn} from '@/utils/common_functions';
import {SuccessCodes} from '@/utils/getMessages';
import React, {PropsWithChildren, useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

interface AccordianLayout extends PropsWithChildren {
  title: string;
  icon: IconType;
  open?: boolean;
  success?: boolean;
  select?: boolean;
  onSelect?: () => void;
}
export const AccordianLayout: React.FC<AccordianLayout> = props => {
  const {children, title, icon, open, success, select, onSelect} = props;

  // ================
  // hooks
  // ================
  const dispatch = useDispatch();

  // ================
  // selectors
  // ================
  const category_id = useSelector(patientDashboardCategory);
  const dashboardSuccess = useSelector(patientDashboardSuccessState) as SuccessCodes;
  const original_category_id = useSelector(patientDashboardOriginalCategory);
  const specialization = useSelector(appSpecializations);
  const allSpecializations = useSelector(allAppSpecializations);
  const step = useSelector(patientDashboardCurrentStep);
  const consultation_id = useSelector(patientDashboardConsultation);
  const specialist = useSelector(patientDashboardSpecialists);
  const consultationFlow = useSelector(patientDashboardConsultationFlow);
  const isManualFlag = useSelector(patientDashboardManualCategorySelectionFlag);
  const hospitalSpecializations = useSelector(patientDashboardHospitalSpecializations);

  // ================
  // states
  // ================
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const valueToShow = useMemo(() => {
    // if follow or refferal the do not show the category untill step is 2 else show the category
    if (step < 2) {
      return null;
    }
    return category_id;
  }, [consultationFlow, step, category_id]);

  const canShowCheckbox = useMemo(() => {
    return (
      consultationFlow !== ConsulationFlowType.HospitalSpecialistAssigned &&
      consultationFlow !== ConsulationFlowType.FollowUp &&
      consultationFlow !== ConsulationFlowType.Referral &&
      consultationFlow !== ConsulationFlowType.HOSPITAL_SPECIALIST_FOLLOWUP &&
      consultationFlow !== ConsulationFlowType.HOSPITAL_SPECIALIST_REFFERAL
    );
  }, [consultationFlow, step, success]);

  // Check if manual category selection is allowed
  const isManualCategorySelectionAllowed = useMemo(() => {
    return (
      consultationFlow !== ConsulationFlowType.HospitalSpecialistAssigned &&
      consultationFlow !== ConsulationFlowType.FollowUp &&
      consultationFlow !== ConsulationFlowType.Referral &&
      consultationFlow !== ConsulationFlowType.HOSPITAL_SPECIALIST_FOLLOWUP &&
      consultationFlow !== ConsulationFlowType.HOSPITAL_SPECIALIST_REFFERAL &&
      step === 2 &&
      !success
    );
  }, [consultationFlow, step, success]);

  // ================
  // functions
  // ================
  const handleGetSpecialistList = (id: number) => {
    if (!consultation_id) return;
    const payload: GetConsultationSpecialistListRequestPayload = {
      specialization_id: id,
      case_id: consultation_id,
    };
    // dispatch the action to get the specialist list and also trigger the api call
    dispatch(getSpecialistListRequest(payload));
    onSelect?.();
  };

  function handleOpen() {
    if (success) {
      setIsOpen(p => !p);
    }
  }

  const handleManualCategorySelection = (checked: boolean) => {
    if (consultation_id && isManualCategorySelectionAllowed) {
      dispatch(manualCategorySelectionRequest({id: consultation_id, is_manual_category_selection: checked}));
    }
  };

  /**
   * side effects
   */

  useEffect(() => {
    setIsOpen(Boolean(open));
  }, [open, success]);

  useEffect(() => {
    let timeout: any;
    if (dashboardSuccess === 'S-10129' && original_category_id) {
      setTimeout(() => {
        dispatch(getPendingConsultationCaseRequest());
      }, 1000);
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [dashboardSuccess]);

  return (
    <div
      style={{boxShadow: '0px 4px 41px -9px #00000033'}}
      className="patient-dashboard-accordion-layout-container bg-white rounded-2xl overflow-hidden py-6 px-4 sm:px-6">
      <div className="flex w-full gap-4 items-center py-4">
        <div className="flex items-center gap-3 sm:gap-4">
          <Icon type={success ? 'success_2' : icon} className={success ? 'size-10' : 'size-8'} />
          <h2 className="text-sm sm:text-base lg:text-xl font-FigtreeBold font-semibold text-ui_blue">{title}</h2>
        </div>

        {select && (
          <div className="select-lg grow">
            <SelectInput
              placeholder="Category"
              disabled={!isManualFlag || success}
              name="category"
              masterOptions={allSpecializations}
              value={valueToShow}
              iconClassName={cn('debug')}
              options={specialization}
              className="min-w-[200px] w-full max-w-[300px] select-input"
              wrapperClassName="py-2 gap-4 border"
              onChange={({id}) => handleGetSpecialistList(id)}
            />
            {canShowCheckbox && (
              <Checkbox
                className="checkbox ml-auto"
                inputClassName="size-5"
                labelClassName="text-nowrap"
                label="Select Category Manually"
                onChange={handleManualCategorySelection}
                checked={isManualFlag}
                disabled={!isManualCategorySelectionAllowed || !consultation_id}
              />
            )}
          </div>
        )}

        {/* Collapse Button */}
        <button onClick={handleOpen} className="ml-auto">
          <Icon type={!open ? 'circle_arrow_down' : 'circle_arrow_up'} className="text-gray-500  cursor-pointer size-5" />
        </button>
      </div>
      {select && (
        <div className="select-sm">
          <SelectInput
            placeholder="Category"
            disabled={!isManualFlag || success}
            name="category"
            masterOptions={allSpecializations}
            value={valueToShow}
            options={specialization}
            className="min-w-[150px] max-w-[230px] select-input"
            wrapperClassName="px-2 py-2 border"
            onChange={({id}) => handleGetSpecialistList(id)}
          />
          {canShowCheckbox && (
            <Checkbox
              className="checkbox ml-auto"
              inputClassName="md:size-5 size-4"
              labelClassName="text-nowrap"
              label="Select Category Manually"
              onChange={handleManualCategorySelection}
              checked={isManualFlag}
              disabled={!isManualCategorySelectionAllowed || !consultation_id}
            />
          )}
        </div>
      )}

      {isOpen && children}
    </div>
  );
};
